import AppObj from 'model/appConfigObj';
import { TaskInitParams } from 'model/taskObj';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faDownload } from '@fortawesome/free-solid-svg-icons';
import videoPlayerApi from './api';
import { desktopAppTaskContex } from 'screen/desktop';
import { fileBasename, isNativeVideoFile } from 'util/openableFile';
import './videoPlayer.css';

export const videoPlayerConfig = {
  appID: 'videoPlayer',
  showName: 'Video Player',
  onList: false,
  icon: require('./images/verticons.png'),
  maxHead: 8,
  req: {
    minSize: { width: 480, height: 320 },
    defSize: 'AUTO' as const,
  },
};

function parseInitParams(parms?: TaskInitParams[]) {
  let videoPath = '';
  let videoList: string[] = [];
  if (parms) {
    for (const p of parms) {
      if (p.command === 'videoPath' && p.value) {
        videoPath = p.value;
      }
      if (p.command === 'videoList' && p.value) {
        try {
          const parsed = JSON.parse(p.value);
          if (Array.isArray(parsed)) {
            videoList = parsed.filter((v) => typeof v === 'string');
          }
        } catch {
          /* ignore */
        }
      }
    }
  }
  if (videoList.length === 0 && videoPath) {
    videoList = [videoPath];
  }
  if (videoPath && !videoList.includes(videoPath)) {
    videoList = [videoPath, ...videoList];
  }
  if (!videoPath && videoList.length > 0) {
    videoPath = videoList[0];
  }
  return { videoPath, videoList };
}

function VideoPlayer(props: { taskInitParms?: TaskInitParams[]; taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const init = useMemo(() => parseInitParams(props.taskInitParms), [props.taskInitParms]);
  const [paths] = useState<string[]>(() =>
    init.videoList.filter((p) => isNativeVideoFile(fileBasename(p)))
  );
  const [index, setIndex] = useState(() => {
    const start = init.videoPath || paths[0] || '';
    const idx = paths.indexOf(start);
    return idx >= 0 ? idx : 0;
  });
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playFailed, setPlayFailed] = useState(false);

  const currentPath = paths[index] || '';

  useEffect(() => {
    if (!props.taskID || !currentPath) {
      return;
    }
    desktopControll?.setTaskTitle?.(props.taskID, fileBasename(currentPath));
  }, [props.taskID, currentPath, desktopControll]);

  useEffect(() => {
    if (!currentPath) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let ticket = '';
    let renewTimer = 0;
    setLoading(true);
    setError(null);
    setPlayFailed(false);
    setSrc(null);

    const attach = (nextTicket: string, expiresInMs: number) => {
      ticket = nextTicket;
      setSrc(videoPlayerApi.filesStreamUrl(nextTicket));
      if (renewTimer) {
        window.clearTimeout(renewTimer);
      }
      const wait = Math.max(30_000, expiresInMs - 60_000);
      renewTimer = window.setTimeout(() => {
        void videoPlayerApi.createMediaTicket(currentPath).then((again) => {
          const next = again.data?.data;
          if (cancelled || !next?.ticket) {
            return;
          }
          const old = ticket;
          attach(next.ticket, next.expiresInMs || wait);
          if (old && old !== next.ticket) {
            void videoPlayerApi.revokeMediaTicket(old);
          }
        }).catch(() => {});
      }, wait);
    };

    void videoPlayerApi
      .createMediaTicket(currentPath)
      .then((res) => {
        const data = res.data?.data;
        ticket = data?.ticket || '';
        if (cancelled) {
          if (ticket) void videoPlayerApi.revokeMediaTicket(ticket);
          return;
        }
        if (!res.data?.success || !ticket) {
          throw new Error(res.data?.message || 'Cannot create playback ticket');
        }
        attach(ticket, data?.expiresInMs || 30 * 60 * 1000);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to open video');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (renewTimer) {
        window.clearTimeout(renewTimer);
      }
      if (ticket) void videoPlayerApi.revokeMediaTicket(ticket);
    };
  }, [currentPath]);

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => (i < paths.length - 1 ? i + 1 : i));
  }, [paths.length]);

  const download = () => {
    if (currentPath) {
      desktopControll?.launchFileDownload?.(currentPath);
    }
  };

  if (paths.length === 0) {
    return (
      <div className="vp-root">
        <div className="vp-empty">No video to play</div>
      </div>
    );
  }

  return (
    <div className="vp-root">
      <div className="vp-toolbar">
        <div className="vp-toolbar-group">
          <button type="button" className="vp-btn" onClick={goPrev} disabled={index <= 0} title="Previous">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button
            type="button"
            className="vp-btn"
            onClick={goNext}
            disabled={index >= paths.length - 1}
            title="Next"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <span className="vp-counter">
            {index + 1} / {paths.length}
          </span>
        </div>
        <div className="vp-toolbar-spacer" />
            <button type="button" className="vp-btn active" onClick={download} title="Download">
          <FontAwesomeIcon icon={faDownload} />
        </button>
      </div>

      <div className="vp-stage">
        {loading && <div className="vp-msg">Loading…</div>}
        {error && !loading && <div className="vp-msg vp-error">{error}</div>}
        {playFailed && !loading && !error && (
          <div className="vp-msg vp-error">
            <p>This video cannot be played in the browser.</p>
            <button type="button" className="vp-download" onClick={download}>
              Download
            </button>
          </div>
        )}
        {!loading && !error && !playFailed && src && (
          <video
            key={src}
            className="vp-video"
            src={src}
            controls
            autoPlay
            onError={() => setPlayFailed(true)}
          />
        )}
      </div>

      <div className="vp-status">
        <span title={currentPath}>{currentPath}</span>
      </div>
    </div>
  );
}

const App: AppObj = {
  config: videoPlayerConfig,
  body: VideoPlayer,
};

export default App;
